import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Plus, Copy, Users, Key, Check, Shield, UserCheck, BarChart3 } from 'lucide-react'
import { formatDateTime } from '@/lib/utils'
import { UserAvatar } from '@/components/Avatar'

export default function AdminPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'dashboard' | 'invites' | 'users'>('dashboard')
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  const { data: inviteCodes } = useQuery({
    queryKey: ['inviteCodes'],
    queryFn: () => api.getInviteCodes(),
  })

  const { data: users } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () => api.getUsers(),
  })

  const { data: leaderboard } = useQuery({
    queryKey: ['adminLeaderboard'],
    queryFn: () => api.getLeaderboard('all'),
  })

  const createInviteMutation = useMutation({
    mutationFn: () => api.createInviteCode(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inviteCodes'] })
    },
  })

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const tabs = [
    { key: 'dashboard' as const, label: '数据概览', icon: BarChart3 },
    { key: 'invites' as const, label: '邀请码管理', icon: Key },
    { key: 'users' as const, label: '用户管理', icon: Users },
  ]

  // 计算统计数据
  const stats = {
    totalUsers: users?.data?.data?.length || 0,
    totalInvites: inviteCodes?.data?.length || 0,
    usedInvites: inviteCodes?.data?.filter((code: any) => code.used).length || 0,
    totalReadings: leaderboard?.data?.reduce((sum: number, entry: any) => sum + entry.totalCount, 0) || 0,
    activeUsers: users?.data?.data?.filter((user: any) => {
      const lastWeek = new Date()
      lastWeek.setDate(lastWeek.getDate() - 7)
      return new Date(user.updated_at) > lastWeek
    }).length || 0,
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* 轻盈背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-32 left-10 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-100/40 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-xs mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">管理后台</h1>
          <p className="text-sm text-gray-600">管理邀请码和用户</p>
        </div>

        {/* 标签页 */}
        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-4 mb-4">
          <div className="grid grid-cols-3 gap-2">
            {tabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex flex-col items-center justify-center py-2 px-2 rounded-xl font-medium transition-all duration-300 ${
                  activeTab === key
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-white/50 backdrop-blur-sm'
                }`}
              >
                <Icon className="h-4 w-4 mb-1" />
                <span className="text-xs">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 数据概览 */}
        {activeTab === 'dashboard' && (
          <div className="space-y-3">
            {/* 统计卡片 */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-3 text-center">
                <Users className="mx-auto h-6 w-6 text-blue-600 mb-2" />
                <div className="text-lg font-semibold text-blue-600">{stats.totalUsers}</div>
                <div className="text-xs text-gray-500 mt-1">总用户数</div>
              </div>

              <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-3 text-center">
                <UserCheck className="mx-auto h-6 w-6 text-green-600 mb-2" />
                <div className="text-lg font-semibold text-green-600">{stats.activeUsers}</div>
                <div className="text-xs text-gray-500 mt-1">活跃用户</div>
              </div>

              <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-3 text-center">
                <Key className="mx-auto h-6 w-6 text-purple-600 mb-2" />
                <div className="text-lg font-semibold text-purple-600">{stats.totalInvites}</div>
                <div className="text-xs text-gray-500 mt-1">邀请码总数</div>
              </div>

              <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-3 text-center">
                <BarChart3 className="mx-auto h-6 w-6 text-red-600 mb-2" />
                <div className="text-lg font-semibold text-red-600">{stats.totalReadings}</div>
                <div className="text-xs text-gray-500 mt-1">总阅读遍数</div>
              </div>
            </div>

            {/* 最近注册用户 */}
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl overflow-hidden">
              <div className="p-4 border-b border-gray-100/50">
                <h3 className="text-base font-semibold text-gray-900">最近注册用户</h3>
              </div>
              {users?.data?.data && users.data.data.length > 0 ? (
                <div className="divide-y divide-gray-100/50 max-h-64 overflow-y-auto">
                  {users.data.data
                    .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .slice(0, 5)
                    .map((user: any) => (
                      <div key={user.id} className="flex items-center justify-between p-3 hover:bg-white/50 transition-colors">
                        <div className="flex items-center space-x-3">
                          {user.avatar ? (
                            <img
                              src={user.avatar}
                              alt={user.username}
                              className="w-8 h-8 rounded-xl object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                              <span className="text-blue-600 font-medium text-sm">
                                {user.username.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{user.username}</p>
                            <p className="text-xs text-gray-500 truncate max-w-32">{user.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{formatDateTime(user.created_at)}</p>
                          {user.is_admin && (
                            <span className="inline-flex items-center px-2 py-1 rounded-xl text-xs font-medium bg-red-100/80 text-red-800 mt-1">
                              <Shield className="w-3 h-3 mr-1" />
                              管理员
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                  <p className="text-gray-500 text-sm">暂无用户数据</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 邀请码管理 */}
        {activeTab === 'invites' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">邀请码列表</h2>
              <button
                onClick={() => createInviteMutation.mutate()}
                disabled={createInviteMutation.isLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>{createInviteMutation.isLoading ? '生成中...' : '生成邀请码'}</span>
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {inviteCodes?.data && inviteCodes.data.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {inviteCodes.data.map((invite: any) => (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-3 h-3 rounded-full ${invite.isUsed ? 'bg-red-500' : 'bg-green-500'}`} />
                        <div>
                          <p className="font-mono text-sm font-medium text-gray-900">{invite.code}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            创建时间：{new Date(invite.createdAt).toLocaleString()}
                          </p>
                          {invite.isUsed && invite.usedAt && (
                            <p className="text-xs text-gray-500">
                              使用时间：{new Date(invite.usedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          invite.isUsed
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {invite.isUsed ? '已使用' : '未使用'}
                        </span>

                        {!invite.isUsed && (
                          <button
                            onClick={() => copyToClipboard(invite.code)}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            {copiedCode === invite.code ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Key className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500">暂无邀请码</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 用户管理 */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">用户列表</h2>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {users?.data?.data && users.data.data.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {users.data.data.map((user: any) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <UserAvatar
                          src={user.avatar}
                          username={user.username}
                          userId={user.id}
                          size="md"
                          lazy={true}
                          priority={false}
                        />
                        <div>
                          <p className="font-medium text-gray-900">{user.username}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                          <p className="text-xs text-gray-400">
                            注册时间：{new Date(user.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {user.isAdmin && (
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            管理员
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500">暂无用户数据</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
